import type { ApolloQueryResult } from "@apollo/client";
import type {
  GetProductsQuery,
  CurrencyCode,
  Scalars,
  Image,
} from "../gql/gen/codegen";
import type { SearchQuery } from "../gql/genNostoSearch/codegen";

import type { Link, CardConnection } from "$lib/gql/genCms/codegen";

export function transformNostoSearchResults(
  searchResults: ApolloQueryResult<SearchQuery>,
  lang: string,
) {
  const result: ApolloQueryResult<GetProductsQuery> = {
    ...searchResults,
    data: {
      products: {
        __typename: "ProductConnection",
        pageInfo: {
          hasNextPage:
            searchResults?.data?.search?.products?.total >
            searchResults?.data?.search?.products?.size +
              searchResults?.data?.search?.products?.from,
          hasPreviousPage: searchResults?.data?.search?.products?.from > 0,
          __typename: "PageInfo",
        },
        edges: searchResults?.data?.search?.products?.hits?.map(hit => {
          return {
            cursor: "",
            node: {
              id: hit.productId,
              title:
                lang === "en"
                  ? hit.name
                  : hit.customFields?.filter(
                        customField =>
                          customField.key === `global-${lang}_title`,
                      )?.[0] !== undefined
                    ? hit.customFields?.filter(
                        customField =>
                          customField.key === `global-${lang}_title`,
                      )?.[0].value
                    : hit.name || "",
              tags: hit.tags1,
              handle: "",
              description: hit.description,
              collections: {
                edges: [
                  {
                    node: {
                      title:
                        hit.extra
                          ?.filter(e => e.key.trim() === "Ecom Cat")
                          .map(e => e.value)
                          .join("")
                          .trim() || "",
                      handle:
                        hit.customFields
                          ?.filter(
                            customField => customField.key === "global-url",
                          )?.[0]
                          ?.value.substring(
                            0,
                            hit.customFields
                              ?.filter(
                                customField => customField.key === "global-url",
                              )?.[0]
                              ?.value.lastIndexOf("/"),
                          ) || "",
                      id: "",
                      canonicalUrl: {
                        value:
                          hit.customFields
                            ?.filter(
                              customField => customField.key === "global-url",
                            )?.[0]
                            ?.value.substring(
                              0,
                              hit.customFields
                                ?.filter(
                                  customField =>
                                    customField.key === "global-url",
                                )?.[0]
                                ?.value.lastIndexOf("/"),
                            ) || "",
                        __typename: "Metafield",
                      },
                      __typename: "Collection",
                    },
                    __typename: "CollectionEdge",
                  },
                ],
                __typename: "CollectionConnection",
              },
              priceRange: {
                minVariantPrice: {
                  amount:
                    hit.skus?.[0]?.customFields
                      ?.filter(
                        customField => customField.key === "skucode",
                      )?.[0]
                      ?.value.includes("GIFT CARD") && hit.skus?.length > 1
                      ? Math.min(...hit.skus.map(sku => sku.price ?? 0))
                      : hit.price,
                  currencyCode: hit.priceCurrencyCode as CurrencyCode,
                  __typename: "MoneyV2",
                },
                maxVariantPrice: {
                  amount: hit.skus?.[0]?.customFields
                    ?.filter(customField => customField.key === "skucode")?.[0]
                    ?.value.includes("GIFT CARD")
                    ? Math.max(...hit.skus.map(sku => sku.price ?? 0))
                    : hit.price,
                  currencyCode: hit.priceCurrencyCode as CurrencyCode,
                  __typename: "MoneyV2",
                },
                __typename: "ProductPriceRange",
              },
              compareAtPriceRange: {
                maxVariantPrice: {
                  amount: hit.listPrice !== hit.price ? hit.listPrice : "0.00",
                  currencyCode: hit.priceCurrencyCode as CurrencyCode,
                  __typename: "MoneyV2",
                },
                minVariantPrice: {
                  amount: hit.listPrice !== hit.price ? hit.listPrice : "0.00",
                  currencyCode: hit.priceCurrencyCode as CurrencyCode,
                  __typename: "MoneyV2",
                },
                __typename: "ProductPriceRange",
              },
              options: [
                {
                  name: "size",
                  values: Array.from(
                    new Set([
                      ...hit.skus
                        .map(sku => {
                          if (
                            sku.customFields
                              .filter(
                                customField => customField.key === "skucode",
                              )?.[0]
                              ?.value.includes("GIFT CARD")
                          )
                            return [
                              lang === "en"
                                ? hit.name
                                : hit.customFields?.filter(
                                      customField =>
                                        customField.key ===
                                        `global-${lang}_title`,
                                    )?.[0] !== undefined
                                  ? hit.customFields?.filter(
                                      customField =>
                                        customField.key ===
                                        `global-${lang}_title`,
                                    )?.[0].value
                                  : hit.name || "",
                            ];
                          return sku.customFields
                            .filter(customField => customField.key === "size")
                            .map(customField => customField.value)
                            .flat();
                        })
                        .flat(),
                    ]),
                  ),
                  __typename: "ProductOption",
                },
                {
                  name: "colour",
                  values: Array.from(
                    new Set([
                      ...hit.skus
                        .map(sku => {
                          if (
                            sku.customFields
                              .filter(
                                customField => customField.key === "skucode",
                              )?.[0]
                              ?.value.includes("GIFT CARD")
                          )
                            return ["-"];
                          return sku.customFields
                            .filter(customField => customField.key === "colour")
                            .map(customField => customField.value)
                            .flat();
                        })
                        .flat(),
                    ]),
                  ),
                  __typename: "ProductOption",
                },
              ],

              material: {
                value:
                  lang === "en"
                    ? hit.customFields?.filter(
                        customField => customField.key === "global-material",
                      )?.[0]?.value
                    : hit.customFields?.filter(
                          customField =>
                            customField.key === `global-${lang}_subtitle`,
                        )?.[0] !== undefined
                      ? hit.customFields?.filter(
                          customField =>
                            customField.key === `global-${lang}_subtitle`,
                        )?.[0].value
                      : hit.customFields?.filter(
                          customField => customField.key === "global-material",
                        )?.[0]?.value || "",
                __typename: "Metafield",
              },

              subtitle: {
                value:
                  lang === "en"
                    ? hit.customFields?.filter(
                        customField => customField.key === "global-material",
                      )?.[0]?.value
                    : hit.customFields?.filter(
                          customField =>
                            customField.key === `global-${lang}_subtitle`,
                        )?.[0] !== undefined
                      ? hit.customFields?.filter(
                          customField =>
                            customField.key === `global-${lang}_subtitle`,
                        )?.[0].value
                      : hit.customFields?.filter(
                          customField => customField.key === "global-material",
                        )?.[0]?.value || "",

                __typename: "Metafield",
              },
              badge: {
                value:
                  hit.customFields?.filter(
                    customField => customField.key === `global-badge`,
                  )?.[0]?.value || "",
              },
              canonicalUrl: {
                value:
                  hit.customFields.filter(
                    customField => customField.key === "global-url",
                  )?.[0]?.value || "",
                __typename: "Metafield",
              },

              images: {
                edges: [
                  {
                    node: {
                      id: "",
                      altText: null,
                      url: hit.imageUrl as Scalars["URL"],
                      __typename: "Image",
                    },
                    __typename: "ImageEdge",
                  },
                ].concat(
                  ...((hit.alternateImageUrls &&
                    hit.alternateImageUrls.length > 0 &&
                    hit.alternateImageUrls.map(altImage => {
                      return {
                        node: {
                          id: "",
                          altText: null,
                          url: altImage as Scalars["URL"],
                          __typename: "Image",
                        },
                        __typename: "ImageEdge",
                      };
                    })) ||
                    []),
                ) as Array<
                  { __typename?: "ImageEdge" } & {
                    node: { __typename?: "Image" } & Pick<
                      Image,
                      "id" | "altText" | "url"
                    >;
                  }
                >,
                __typename: "ImageConnection",
              },
              variants: {
                edges: hit.skus?.map(sku => {
                  return {
                    node: {
                      id: `gid://shopify/ProductVariant/${sku.id}`,
                      selectedOptions: [
                        {
                          name: "size",
                          value: sku.customFields
                            .filter(
                              customField => customField.key === "skucode",
                            )?.[0]
                            ?.value.includes("GIFT CARD")
                            ? sku.name
                            : sku.customFields.filter(
                                customField => customField.key === "size",
                              )?.[0]?.value,
                          __typename: "SelectedOption",
                        },
                        {
                          name: "colour",
                          value:
                            sku.customFields.filter(
                              customField => customField.key === "colour",
                            )?.[0]?.value || "-",
                          __typename: "SelectedOption",
                        },
                      ],
                      sku: sku.customFields.filter(
                        customField => customField.key === "skucode",
                      )?.[0]?.value,
                      title: sku.name,
                      price: {
                        amount: sku.customFields
                          .filter(
                            customField => customField.key === "skucode",
                          )?.[0]
                          ?.value.includes("GIFT CARD")
                          ? sku.price
                          : hit.price,
                        currencyCode: hit.priceCurrencyCode as CurrencyCode,
                        __typename: "MoneyV2",
                      },
                      quantityAvailable: sku.customFields
                        .filter(
                          customField => customField.key === "skucode",
                        )?.[0]
                        ?.value.includes("GIFT CARD")
                        ? 999999999
                        : sku.inventoryLevel,
                      __typename: "ProductVariant",
                    },
                    __typename: "ProductVariantEdge",
                  };
                }),
                __typename: "ProductVariantConnection",
              },
              sizeGuide:
                hit.customFields?.filter(
                  customField => customField.key === "global-sizeguide",
                )?.[0]?.value || "",
              __typename: "Product",
            },
            __typename: "ProductEdge",
          };
        }),
      },
    },
  };
  return result;
}

export function transformNostoFilterOptions(
  searchResults: ApolloQueryResult<SearchQuery>,
) {
  const result = searchResults.data?.search?.products?.facets.map(facet => {
    if (facet.type === "terms") {
      return {
        label: facet.name,
        field: facet.field,
        options: facet.data,
      };
    } else {
      return {
        label: facet.name,
        field: facet.field,
        min: facet.min,
        max: facet.max,
      };
    }
  });

  return result;
}

export function handleFilterOOSVariants(filters: any, query: any) {
  if (filters && filters.length === 0) return query;

  let products = query.data.search.products.hits;

  filters.forEach(filter => {
    const filterAccessors = filter.field.split(".");

    products = products.map(product => {
      if (filterAccessors[0] === "skus") {
        const variants = product.skus;

        let filterProduct = true;

        variants.forEach(variant => {
          if (
            variant[filterAccessors[1]] &&
            Array.isArray(variant[filterAccessors[1]])
          ) {
            const option = variant[filterAccessors[1]].filter(
              field => field.key === filterAccessors[2],
            )?.[0]?.value;

            if (
              filter.value.includes(option) &&
              variant.availability === "InStock"
            ) {
              filterProduct = false;
            }
          } else {
            const accessor = filterAccessors.slice(1);
            const option = accessor.reduce((acc, curr) => {
              return acc[curr];
            }, variant);

            if (
              filter.value.includes(option) &&
              variant.availability === "InStock"
            ) {
              filterProduct = false;
            }
          }
        });

        if (filterProduct === true) {
          return null;
        } else {
          return product;
        }
      } else {
        return product;
      }
    });
  });
  return {
    ...query,
    data: {
      ...query.data,
      search: {
        ...query.data.search,
        products: {
          ...query.data.search.products,
          hits: products.filter(product => product !== null),
        },
      },
    },
  };
}

export function transformNostoSearchCard(
  results: ApolloQueryResult<GetProductsQuery>,
): CardConnection {
  const count = results.data.products.edges.length || 0;
  return {
    __typename: "CardConnection",
    edges: results.data.products.edges.map(edge => {
      return {
        node: {
          content: {
            product: {
              ...edge.node,
              image: {
                ...edge.node?.images?.edges?.[0],
                originalSrc: edge.node?.images?.edges?.[0]?.node?.url,
              },
              showPrice: true,

              subtitle: edge.node?.material?.value,
              badge: edge.node.badge.value,
              canonicalUrl: edge.node.canonicalUrl.value,
              __typename: "Product",
            },
            __typename: "ProductCard",
          },
          type: "PRODUCT",
          __typename: "Card",
        },
        __typename: "CardEdge",
      };
    }),
    totalCount: count,
  };
}
